import { Alert, App as AntApp, Button, DatePicker, Drawer, Form, Grid, Input, InputNumber, Select, Space, Typography } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { classKeys, defaultYear, useAcademicYears, useActiveTeachers } from "../app/classes.ts";
import { EXAM_NAME_MAX_LENGTH, examKeys, useSubjects, type ExamDetails } from "../app/exams.ts";
import { MAX_SCORE_LIMIT, MIN_SCORE, useAssessmentTypes } from "../app/grading.ts";

interface Props {
  open: boolean;
  /** The exam to change; undefined plans a new one. */
  exam?: ExamDetails | undefined;
  onClose(): void;
  onSaved?(exam: ExamDetails): void;
}

interface ExamForm {
  name?: string;
  date?: Dayjs;
  subjectId?: string;
  maxScore?: number | null;
  classes?: { classId?: string; graderTeacherId?: string }[];
}

/**
 * Planning a school-wide exam (bd-3qg.10, academic.manage): name, date, subject, maximum mark (empty means the Exam assessment
 * type's maximum) and the classes with the teacher who grades each, who may be any teacher. Classes come from the academic year
 * that contains the chosen date. The server checks everything again and its field errors land on the matching inputs.
 */
export function ExamDrawer({ open, exam, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ExamForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const screens = Grid.useBreakpoint();
  const date = Form.useWatch("date", form);
  const years = useAcademicYears();
  const subjects = useSubjects(open);
  const teachers = useActiveTeachers(open);
  const types = useAssessmentTypes();
  const examTypeMax = types.data?.find((type) => type.kind === "Exam")?.maxScore;
  const chosen = date ? date.format("YYYY-MM-DD") : undefined;
  const year = (chosen ? years.data?.find((y) => y.startDate <= chosen && chosen <= y.endDate) : undefined) ?? defaultYear(years.data);
  const classes = useQuery({
    queryKey: classKeys.list(year?.id),
    enabled: open && year !== undefined,
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes", { params: { query: year ? { AcademicYearId: year.id } : {} } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setSummary([]);
    form.setFieldsValue(
      exam
        ? {
            name: exam.name,
            date: dayjs(exam.date),
            subjectId: exam.subjectId,
            maxScore: Number(exam.maxScore),
            classes: exam.classes.map((c) => ({ classId: c.classId, graderTeacherId: c.graderTeacherId })),
          }
        : { classes: [{}] },
    );
  }, [open, exam, form]);

  const mutation = useMutation({
    mutationFn: async (values: ExamForm) => {
      const body = {
        name: values.name?.trim() ?? "",
        date: values.date ? values.date.format("YYYY-MM-DD") : null,
        subjectId: values.subjectId ?? null,
        maxScore: typeof values.maxScore === "number" ? values.maxScore : null,
        classes: (values.classes ?? []).map((c) => ({ classId: c.classId ?? null, graderTeacherId: c.graderTeacherId ?? null })),
      };
      const { data, error, response } = exam
        ? await api.PUT("/grading/exams/{examId}", { params: { path: { examId: exam.id } }, body })
        : await api.POST("/grading/exams", { body });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: examKeys.all });
      void message.success(t("exams.saved"));
      onSaved?.(saved);
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const required = { required: true, message: t("common.required") };
  const teacherOptions = (teachers.data ?? []).map((teacher) => ({ value: teacher.id, label: teacher.fullName }));

  return (
    <Drawer
      open={open}
      title={exam ? t("exams.editTitle") : t("exams.createTitle")}
      onClose={onClose}
      size={screens.md ? 640 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("common.save")}
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">{t("exams.planHint")}</Typography.Paragraph>
      {summary.length > 0 && <Alert type="error" showIcon title={summary.join(" ")} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate(values)}>
        <Form.Item name="name" label={t("exams.name")} rules={[{ ...required, whitespace: true }, { max: EXAM_NAME_MAX_LENGTH }]}>
          <Input maxLength={EXAM_NAME_MAX_LENGTH} placeholder={t("exams.namePlaceholder")} />
        </Form.Item>
        <Space wrap size="middle" align="start">
          <Form.Item name="date" label={t("exams.date")} rules={[required]}>
            <DatePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="subjectId" label={t("exams.subject")} rules={[required]}>
            <Select
              style={{ width: 240 }}
              loading={subjects.isPending}
              showSearch={{ optionFilterProp: "label" }}
              options={(subjects.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>
          <Form.Item
            name="maxScore"
            label={t("exams.maxScore")}
            extra={examTypeMax === undefined ? undefined : t("exams.maxScoreHint", { max: Number(examTypeMax) })}
          >
            <InputNumber min={MIN_SCORE} max={MAX_SCORE_LIMIT} precision={0} style={{ width: 140 }} />
          </Form.Item>
        </Space>
        <Typography.Title level={5}>{t("exams.classesTitle")}</Typography.Title>
        <Typography.Paragraph type="secondary">{t("exams.graderHint")}</Typography.Paragraph>
        <Form.List name="classes">
          {(fields, { add, remove }, { errors }) => (
            <>
              {fields.map((field, index) => (
                <Space key={field.key} wrap align="start" style={{ display: "flex" }}>
                  <Form.Item name={[field.name, "classId"]} label={index === 0 ? t("exams.class") : undefined} rules={[required]}>
                    <Select
                      aria-label={t("exams.classNumbered", { n: index + 1 })}
                      style={{ width: 160 }}
                      loading={classes.isPending && classes.fetchStatus !== "idle"}
                      showSearch={{ optionFilterProp: "label" }}
                      options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
                    />
                  </Form.Item>
                  <Form.Item name={[field.name, "graderTeacherId"]} label={index === 0 ? t("exams.grader") : undefined} rules={[required]}>
                    <Select
                      aria-label={t("exams.graderNumbered", { n: index + 1 })}
                      style={{ width: 260 }}
                      loading={teachers.isPending}
                      showSearch={{ optionFilterProp: "label" }}
                      options={teacherOptions}
                    />
                  </Form.Item>
                  <Button
                    type="text"
                    icon={<MinusCircleOutlined />}
                    aria-label={t("exams.removeClass", { n: index + 1 })}
                    disabled={fields.length === 1}
                    onClick={() => remove(field.name)}
                    style={index === 0 ? { marginTop: 30 } : undefined}
                  />
                </Space>
              ))}
              <Form.ErrorList errors={errors} />
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})}>
                {t("exams.addClass")}
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Drawer>
  );
}
